import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@heroui/react';
import { IDcfVessel, TFinanceProjectAssumptions, TFinancialMetrics, TVesselDcf } from '../dcfTypes';
import DynamicTab from '../../../shared/components/tabs/DynamicTab';
import VesselSummary from '../components/VesselSummary';
import VesselAssumptions from '../components/VesselAssumptions';
import { ITabPage } from '../../../shared/components/tabs/ITabPage';
import DCFNote from '../components/DcfNote';
import { TDcfScenarioParams, backCalcEarnings, computeLiveMetrics } from '../dcfCalculations';
import { exportDcfToExcel } from '../dcfExport';
import logoSvgRaw from '../../../assets/images/logos/logo_dcf.svg?raw';
import { FaRegFileExcel } from 'react-icons/fa';
import { RiResetRightFill } from 'react-icons/ri';

export interface IVesselDcf extends ITabPage<IDcfVessel> {
	vesselDcf: TVesselDcf | null;
}

const DEFAULT_BASE_RATE = 4.2;

// WACC is derived, not user-set: (1 − LTV) × Cost of Equity + LTV × (Base Rate + Interest Margin).
// All inputs are in percentage points, so the result is a percentage too.
function computeWaccPct(assumptions: TFinanceProjectAssumptions, ltv: number): number {
	const ltvFrac = ltv / 100;
	const coe = assumptions.costOfEquity ?? 0;
	const baseRate = assumptions.baseRate ?? DEFAULT_BASE_RATE;
	const margin = assumptions.interestRateMargin ?? 0;
	return (1 - ltvFrac) * coe + ltvFrac * (baseRate + margin);
}

function buildScenarioParams(assumptions: TFinanceProjectAssumptions, ltv: number): TDcfScenarioParams {
	return {
		purchase: assumptions.purchasePrice ?? 0,
		sale: assumptions.salePrice ?? 0,
		wacc: computeWaccPct(assumptions, ltv) / 100,
		coe: (assumptions.costOfEquity ?? 0) / 100,
		numQuarters: (assumptions.period ?? 5) * 4,
		ltv: ltv / 100,
		baseRate: (assumptions.baseRate ?? DEFAULT_BASE_RATE) / 100,
		margin: (assumptions.interestRateMargin ?? 0) / 100,
		opex: assumptions.averageOpex ?? 0,
		earnings: assumptions.averageEarnings ?? 0,
		balloon: assumptions.balloon ?? 0,
		onHire: assumptions.onHire ?? 355,
		operatingDays: assumptions.operatingDays ?? 365,
	};
}

const VesselDcf: React.FC<IVesselDcf> = ({ ...props }) => {
	const originalAssumptions = useRef<TFinanceProjectAssumptions | null>(null);
	const originalLtv = useRef<number>(0);

	const [editedAssumptions, setEditedAssumptions] = useState<TFinanceProjectAssumptions | null>(null);
	const [editedLtv, setEditedLtv] = useState<number>(0);
	const [isEdited, setIsEdited] = useState(false);

	useEffect(() => {
		const fa = props.vesselDcf?.financeProjectAssumptions;
		const ltv = props.vesselDcf?.financialMetrics?.ltvAtFinance ?? 0;
		const dbProjectDcfValue = props.vesselDcf?.financialMetrics?.projectDcfValue;
		if (!fa) return;

		const withDefaults = { ...fa, baseRate: fa.baseRate ?? DEFAULT_BASE_RATE };

		// Back-calculate the flat earnings figure that reproduces the DB's projectDcfValue.
		// This aligns the UI calculation with the DB result on initial load and after reset.
		const params = buildScenarioParams(withDefaults, ltv);
		const impliedEarnings = dbProjectDcfValue != null
			? parseFloat(backCalcEarnings(params, dbProjectDcfValue).toFixed(2))
			: (fa.averageEarnings ?? 0);

		const baselineAssumptions = { ...withDefaults, averageEarnings: impliedEarnings };
		originalAssumptions.current = baselineAssumptions;
		originalLtv.current = ltv;
		setEditedAssumptions(baselineAssumptions);
		setEditedLtv(ltv);
		setIsEdited(false);
	}, [props.vesselDcf]);

	const liveMetrics = useMemo((): TFinancialMetrics => {
		// Before useEffect has run, fall back to raw DB metrics to avoid a blank flash
		if (!editedAssumptions) {
			return props.vesselDcf?.financialMetrics ?? {
				projectDcfValue: null, equityDcfValue: null,
				projectIrr: null, equityIrr: null,
				projectNpv: null, equityNpv: null, ltvAtFinance: null,
			};
		}

		const params = buildScenarioParams(editedAssumptions, editedLtv);
		const live = computeLiveMetrics(params);
		const safe = (v: number) => (isFinite(v) ? parseFloat(v.toFixed(2)) : null);
		const safePct = (v: number | null) => (v != null && isFinite(v) ? parseFloat((v * 100).toFixed(2)) : null);
		return {
			projectDcfValue: safe(live.projectDcfValue),
			equityDcfValue: safe(live.equityDcfValue),
			projectIrr: safePct(live.projectIrr),
			equityIrr: safePct(live.equityIrr),
			projectNpv: props.vesselDcf?.financialMetrics?.projectNpv ?? null,
			equityNpv: props.vesselDcf?.financialMetrics?.equityNpv ?? null,
			ltvAtFinance: editedLtv,
		};
	}, [editedAssumptions, editedLtv, props.vesselDcf]);

	// Assumptions shown in the panel, with WACC overridden by its live calculation
	const displayAssumptions = useMemo(
		() => (editedAssumptions ? { ...editedAssumptions, wacc: parseFloat(computeWaccPct(editedAssumptions, editedLtv).toFixed(2)) } : null),
		[editedAssumptions, editedLtv],
	);

	const handleAssumptionChange = useCallback((field: keyof TFinanceProjectAssumptions, value: number) => {
		setIsEdited(true);
		setEditedAssumptions((prev) => (prev ? { ...prev, [field]: value } : prev));
	}, []);

	const handleLtvChange = useCallback((value: number) => {
		setIsEdited(true);
		setEditedLtv(value);
	}, []);

	const handleReset = useCallback(() => {
		if (!originalAssumptions.current) return;
		setEditedAssumptions({ ...originalAssumptions.current });
		setEditedLtv(originalLtv.current);
		setIsEdited(false);
	}, []);

	const handleExport = useCallback(() => {
		if (!editedAssumptions) return;
		const params = buildScenarioParams(editedAssumptions, editedLtv);
		const vesselName = props.data?.name ?? 'Vessel';
		void exportDcfToExcel(params, vesselName, logoSvgRaw);
	}, [editedAssumptions, editedLtv, props.data]);

	const pageContent = useMemo(() => {
		return (
			<div className="flex flex-col w-full gap-3">
				{/* Header row: label left, buttons right */}
				<div className="flex items-center justify-between ml-1">
					<span className="font-semibold">Scenario Calculator</span>
					<div className="flex gap-2">
						{isEdited && (
							<Button
								className="border-msi-orange-color text-msi-orange-color"
								size="sm"
								variant="bordered"
								color="warning"
								onPress={handleReset}
							>
								<RiResetRightFill className="mr-1" />
								Reset to MSI forecast
							</Button>
						)}
						<Button size="sm" variant="bordered" isDisabled={!editedAssumptions} onPress={handleExport} color="default">
							<FaRegFileExcel className="mr-1" />
							Export to Excel
						</Button>
					</div>
				</div>

				{/* Cards row — stretch so both cards share the same height */}
				<div className="flex w-full flex-row gap-4 items-stretch">
					<div className="flex-1">
						<VesselSummary financialMetrics={liveMetrics} />
					</div>
					{displayAssumptions && (
						<div className="flex-1">
							<VesselAssumptions
								financialMetrics={liveMetrics}
								financeProjectAssumptions={displayAssumptions}
								ltvAtFinance={editedLtv}
								onChange={handleAssumptionChange}
								onLtvChange={handleLtvChange}
							/>
						</div>
					)}
				</div>

				<DCFNote />
			</div>
		);
	}, [liveMetrics, editedAssumptions, displayAssumptions, editedLtv, isEdited, handleAssumptionChange, handleLtvChange, handleReset, handleExport]);

	const tabs = useMemo(
		() => [
			{
				id: 'overview',
				label: 'Overview',
				content: pageContent,
			},
		],
		[pageContent],
	);

	return (
		<div className="flex flex-col w-full h-full">
			{props.hideTab ? pageContent : <DynamicTab items={tabs} tabColor="msi-chart-orange" />}
		</div>
	);
};

export default VesselDcf;
